import React, { useContext, useState } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames/bind';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  bool,
  shape,
  string as stringType,
  arrayOf,
  params, useDataModel, extend
} from '@thd-nucleus/data-sources';
import {
  Col,
  Placeholder,
  Row
} from '@thd-olt-component-react/core-ui';
import { StoreHeroContext } from '../../../StoreHero.context';
import { FeatureBlock } from '../feature-block/FeatureBlock';
import { Layout } from '../Layout';
import { FEATURES_MAP, getRentalService } from './features-list-data';
import styles from './features-list.module.scss';

const cx = classNames.bind(styles);

const FeaturesList = ({ className }) => {
  const [storeOverlayOpen, setStoreOverlayOpen] = useState(false);

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const { store } = useContext(StoreHeroContext);
  const { name, storeId } = store || {};
  const storeSearchPath = '/l/search/' + storeId;
  const options = {
    variables: {
      storeId
    },
    skip: !storeId
  };
  const { data, loading, error } = useDataModel('seoStoreRentals', options);
  const { programs } = data?.seoStoreRentals || {};
  const { toolsAndTrucks, noShow } = data?.seoStoreRentals?.programs || {};
  const storeSearchOptions = {
    variables: {
      pagesize: '1',
      storeSearchInput: storeId
    },
    skip: !storeId
  };
  const {
    data: storeServicesData,
    loading: isStoreServicesLoading,
    error: storeServicesError
  } = useDataModel('storeSearch', storeSearchOptions);
  const storeServices = storeServicesData?.storeSearch?.stores?.[0].services;
  const feature = getRentalService(storeServices, toolsAndTrucks);
  const hasData = data && !noShow;
  const hasDataNoShow = data && noShow;
  const hasDataNoPrograms = data && !programs;
  const showLoader = !data || loading || isStoreServicesLoading;

  return (
    <div>
      <div className={cx(className, 'features-list__wrapper')}>
        <FeatureBlock feature={FEATURES_MAP.home} />
        {showLoader && (
          <div className={cx('features-list__loader')}>
            <Row className={cx('features-list__row')}>
              <Col nopadding fallback={8}>
                <Placeholder height="20px" className={cx('features-list__loader')} />
              </Col>
              <Col className={cx('features-list__loader-wrapper')} nopadding fallback={4}>
                <Placeholder height="40px" width="40px" className={cx('features-list__loader--rect')} />
              </Col>
            </Row>
          </div>
        )}
        {hasDataNoShow && (<FeatureBlock feature={FEATURES_MAP.noRental} />)}
        {hasData && (
          <FeatureBlock feature={feature || FEATURES_MAP.noRental} />
        )}
        {hasDataNoPrograms && (
          <FeatureBlock feature={FEATURES_MAP.default} />
        )}
        <FeatureBlock feature={FEATURES_MAP.garden} />
        {isMobile && (
          <FeatureBlock
            feature={FEATURES_MAP.storeLayout}
            setStoreOverlayOpen={setStoreOverlayOpen}
          />
        )}
      </div>
      {storeOverlayOpen && (
        <Layout storeId={storeId} storeName={name} toggleOverlay={setStoreOverlayOpen} />
      )}
    </div>
  );
};

FeaturesList.propTypes = {
  className: string
};

FeaturesList.defaultProps = {
  className: null
};

FeaturesList.displayName = 'FeatureList';

FeaturesList.dataModel = extend({
  seoStoreRentals: params({ storeId: stringType().isRequired() }).shape({
    programs: shape({
      toolsAndTrucks: bool(),
      noShow: bool(),
      onlyTools: bool(),
      onlyTrucks: bool(),
      justMoving: bool()
    }),
    rentals: arrayOf(shape({
      category: stringType()
    }))
  }),
  storeSearch: params({ pagesize: stringType().isRequired(), storeSearchInput: stringType().isRequired() }).shape({
    stores: arrayOf(shape({
      services: shape({
        hdMoving: bool(),
        largeEquipment: bool(),
        loadNGo: bool(),
        penske: bool(),
        propane: bool(),
        // rugDoctor: bool(), //TODO: Need rug doctor in the BE.
        toolRental: bool(),
        // toolRentalLite: bool(), //TODO: Need tool RentalLite in the BE.
      })
    }))
  })
});

export { FeaturesList };
